import { action, observable, makeObservable, computed } from 'mobx';

import { IClientConfigBackground } from 'APP/model/auth/authModel.types';
import { IHostConfig } from 'APP/model/config/configModel.types';

interface IMessageKeywordData {
  animationURL: string;
}

export enum ClientConfigStoreKey {
  AdminAuthUrlBase = 'admin-auth-url-base',
  OnboardingCollectionsIntro = 'collections_intro',
  SupportEmailKey = 'support-email',
  TelegramConfigUri = 'telegram_uri',
  TelegramConfigToken = 'telegram_token',
  Backgrounds = 'backgrounds',
}

interface IClientConfigStoreValue {
  [ClientConfigStoreKey.AdminAuthUrlBase]: string;
  [ClientConfigStoreKey.OnboardingCollectionsIntro]: string;
  [ClientConfigStoreKey.SupportEmailKey]: string;
  [ClientConfigStoreKey.TelegramConfigUri]: string;
  [ClientConfigStoreKey.TelegramConfigToken]: string;
  [ClientConfigStoreKey.Backgrounds]: IClientConfigBackground[];
}

type InferClientConfigValue<K extends ClientConfigStoreKey> = IClientConfigStoreValue[K];

const INITIAL_HOST_CONFIG: IHostConfig = {
  bffUrl: '',
  wsApiUrl: '',
  liveKitCallsUrl: '',
  unauthorizedUrl: '',
  supportEmail: '',
  hostName: '',
};

export class ClientConfig {
  messageKeywords = new Map<string, IMessageKeywordData>();
  @observable config = observable.map<
    ClientConfigStoreKey,
    IClientConfigStoreValue[ClientConfigStoreKey]
  >({}, { deep: false });
  @observable slowMode = {
    endSlowModeTs: 0,
    duration: 0,
  };
  hostConfig: IHostConfig = INITIAL_HOST_CONFIG;

  constructor() {
    makeObservable(this);
  }

  @action
  setSlowMode(endSlowModeTs = 0, duration = 0): void {
    this.slowMode = {
      endSlowModeTs,
      duration,
    };
  }

  addMessageKeywords(type: string, animationURL: string): void {
    this.messageKeywords.set(type, { animationURL });
  }

  getMessageKeywords(type: string): { animationURL: string } | null {
    return this.messageKeywords.get(type) || null;
  }

  @action
  setConfig<K extends ClientConfigStoreKey>(key: K, value: InferClientConfigValue<K>): void {
    this.config.set(key, value);
  }

  @computed
  get backgroundsMap(): Record<string, IClientConfigBackground> {
    const backgrounds = this.getConfig(ClientConfigStoreKey.Backgrounds) || [];

    return backgrounds.reduce<Record<string, IClientConfigBackground>>((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }

  getConfig<K extends ClientConfigStoreKey>(key: K): InferClientConfigValue<K> | undefined {
    return this.config.get(key) as InferClientConfigValue<K> | undefined;
  }

  setHostConfig(hostConfig: IHostConfig): void {
    this.hostConfig = hostConfig;
  }

  resetHostConfig(): void {
    this.hostConfig = INITIAL_HOST_CONFIG;
  }
}
